import { Badge, BadgeColour, BadgeType } from '@xemplo/badge';
import { useGetIntegrationSettingsQuery } from '@xemplo/business-unit-query';
import { IconButton, IconButtonSize } from '@xemplo/icon-button';
import { DawnPencilEdit, DawnSettings, IconGradient } from '@xemplo/icons';
import { CenteredLoaderDots } from '@xemplo/loader';

import { SETTINGS_SECTIONS } from '../business-unit-settings.config';
import * as S from '../business-unit-settings.styles';
import { SettingsCardProps } from '../business-unit-settings.types';

function StatusBadge({ enabled }: Readonly<{ enabled: boolean }>) {
  return (
    <Badge
      value={enabled ? 'Enabled' : 'Disabled'}
      type={BadgeType.DefaultPip}
      color={enabled ? BadgeColour.Green : BadgeColour.Gray}
    />
  );
}

export function SettingsCard(props: Readonly<SettingsCardProps>) {
  const { businessUnitId, viewOnly, onEdit, className } = props;
  const { data, isLoading } = useGetIntegrationSettingsQuery({ businessUnitId });
  const settings = data?.settings;
  if (isLoading) return <CenteredLoaderDots />;
  if (!settings) return null;
  return (
    <S.CardContainer className={className}>
      <S.Header>
        <S.IconWrapper>
          <DawnSettings fontSize={24} />
          <IconGradient />
        </S.IconWrapper>
        <S.Title>Settings</S.Title>
        {!viewOnly && (
          <IconButton
            id="edit-business-unit-settings-button"
            ariaLabel="Edit settings"
            size={IconButtonSize.Small}
            onClick={onEdit}
          >
            <DawnPencilEdit />
          </IconButton>
        )}
      </S.Header>

      <S.Sections>
        {SETTINGS_SECTIONS.map((section) => (
          <S.Section key={section.id}>
            <S.SectionHeading>
              <S.SectionTitle>{section.title}</S.SectionTitle>
              <S.SectionDescription>{section.description}</S.SectionDescription>
            </S.SectionHeading>

            <S.SettingsGroup>
              {section.settings.map((setting) => (
                <S.SettingRow key={setting.settingName}>
                  <S.SettingLabel>
                    <S.Label>{setting.label}</S.Label>
                    {setting.helperText && (
                      <S.HelperText>{setting.helperText}</S.HelperText>
                    )}
                  </S.SettingLabel>
                  <StatusBadge enabled={settings[setting.settingName] ?? false} />
                </S.SettingRow>
              ))}
            </S.SettingsGroup>
          </S.Section>
        ))}
      </S.Sections>
    </S.CardContainer>
  );
}

export default SettingsCard;
